// Session 28: a page heading with its own "how it works".
//
// Reuses machinery that already existed and had never been used:
// portal_explainer_dismissals, portal_dismiss_explainer, and
// explainers_dismissed on v_portal_me.
//
// Two things the Session 13 version on the Plan page does not do, both from
// Leena's decisions on 2026-10-09:
//
//   1. **There is a way back.** Dismissed, it collapses to a question mark
//      beside the heading; clicking that reopens it. The old one vanished for
//      good, which is why she asked for this. Reopening is local only and does
//      not un-dismiss server side, so it stays out of the way next visit.
//   2. **Agency users get it collapsed.** Expanding on every client switch
//      would be noise, so for an agency user it starts as the question mark
//      and opens only on a click. The first version suppressed it entirely,
//      which had a consequence neither of us saw coming: Leena could not
//      review her own copy, because she is the only agency user. Collapsed,
//      not absent, gives both.
//
// This component renders the heading itself rather than sitting next to one.
// That is deliberate: a <section> cannot live inside an <h1>, so the only way
// to get the question mark genuinely *in* the heading, with one instance and
// one piece of state, is for the heading to be part of the component.
//
// The Session 13 HowItWorks on the Plan page is left alone. It is approval-mode
// aware and it works.

import { useState } from 'react';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { explainerFor } from '../explainers/index.js';

export default function SectionHeading({ title, explainer, label, level = 1 }) {
  const { me, isAgency } = useAuth();
  const [dismissedNow, setDismissedNow] = useState(false);
  const [reopened, setReopened] = useState(false);
  const [busy, setBusy] = useState(false);

  const H = level === 2 ? 'h2' : 'h1';
  const content = explainer ? explainerFor(explainer) : null;

  // No explainer or an unknown id: just the heading. An unknown id is a
  // content bug, so the agency is told rather than left wondering why nothing
  // appears.
  if (!explainer || !content) {
    return (
      <>
        <H>{title}</H>
        {explainer && !content && isAgency && (
          <p className="hint">Agency note: no explainer content for &ldquo;{explainer}&rdquo;.</p>
        )}
      </>
    );
  }

  // An agency user always starts collapsed and never records a dismissal:
  // there is nothing to remember, and it must not fire on every client switch.
  const dismissed = isAgency
    ? true
    : dismissedNow || (me?.explainers_dismissed || []).includes(explainer);
  const open = reopened || !dismissed;

  async function dismiss() {
    // Nothing to store for an agency user: they are previewing, not reading.
    if (isAgency) { setReopened(false); return; }
    setBusy(true);
    const { error } = await supabase.rpc('portal_dismiss_explainer', { p_key: explainer });
    setBusy(false);
    setReopened(false);
    setDismissedNow(true);
    // A failed dismiss still closes it for this visit. Nagging someone who
    // just asked you to go away is worse than forgetting that they did.
    if (error) console.warn('[explainer] dismiss:', error.message);
  }

  return (
    <>
      <H>
        {title}
        {!open && (
          <button
            type="button"
            className="explainer-reopen"
            aria-label={isAgency
              ? `Preview what a client reads about ${label || 'this'}`
              : `How ${label || 'this'} works`}
            title={isAgency
              ? `Preview: what a client reads about ${label || 'this'}`
              : `How ${label || 'this'} works`}
            onClick={() => setReopened(true)}
          >
            ?
          </button>
        )}
      </H>
      {open && (
        <section className="explainer" aria-label={content.title}>
          <div className="explainer-head">
            <h2>{content.title}{isAgency && <span className="explainer-tag">what the client sees</span>}</h2>
            <button type="button" className="link-btn" onClick={dismiss} disabled={busy}>
              {busy ? 'Closing…' : 'Got it, hide this'}
            </button>
          </div>
          {content.intro && <p>{content.intro}</p>}
          {content.steps?.length > 0 && (
            <ol>{content.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          )}
          {content.outro && <p className="explainer-outro">{content.outro}</p>}
        </section>
      )}
    </>
  );
}
